import React from "react";
import { Button, Pagination, Select } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { get, isNull } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import UserSearchFilter from "~/components/Filter/UserSearchFilter";
import EditPositionDirection from "~/pages/Logs/components/EditPositionDirection";
import Wrapper from "~/shared/ui/Wrapper";
import useVersionContextSpecificSet from "~/versionControl/hooks/useVersionContextSpecificSet";
import { EVENT_OPTIONS } from "~/constans/events";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { useSetSelectFilterOption } from "~/hooks/SelectValue";
import { diffJSON, getIndicatorName } from "~/libs/getData";
import { getFormatDate } from "~/libs/getFormatDate";
import { LOG_RESOLVE_REASON, logFieldResolver } from "~/pages/Logs/lib";
import { LOG_OPTIONS } from "~/pages/Logs/options";
import { useGetLogsQuery } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { filterOptionByLabel } from "~/shared/lib/filterHelpers";
import { TableV2 } from "~/shared/ui/TableV2";

import s from "./styles.less";

const Logs = () => {
  const { t } = useTranslation();
  const { page, count, event, openModal, UserId, searchText } =
    useGetLocationData();
  const selectFilterOption = useSetSelectFilterOption();
  const setData = useSetLocationData();
  const { handleCount, handlePage } = usePaginationParams();

  const { hasPermission } = usePermissions();

  const history = useHistory();
  const goBack = () => {
    history.replace("/logs/email");
  };
  const goLogsIvideon = () => {
    history.replace("/logs/ivideon");
  };

  const goIntegrationsLogs = () => {
    history.replace("/logs/integrations");
  };

  const { data, loading } = useGetLogsQuery({
    variables: {
      page,
      count,
      event,
      UserId,
    },
    fetchPolicy: "no-cache",
  });

  const getDiffs = ({ data, description }) => {
    const { prevValue, nextValue, event } = data;

    if (event === "edit_position_direction") {
      return <EditPositionDirection object1={prevValue} object2={nextValue} />;
    }

    if (
      event === "delete_position_group_education_program" ||
      event === "add_position_group_education_program"
    ) {
      return description;
    }

    const changeFormatData = (obj) => {
      for (const key in obj) {
        if (["updatedAt", "lastOnlineDate"].includes(key)) {
          obj[key] = getFormatDate(obj[key]);
        }
        if (
          [
            "createdAt",
            "dateBegin",
            "beginWorkingDate",
            "uploadDate",
            "lastEditDate",
            "HiredDate",
            "FiredDate",
            "startDate",
            "endDate",
            "birthDay",
            "reportDate",
            "lastDate",
            "DocDate",
            "fixDateAt",
            "ActualTo",
            "ActualFrom",
            "date",
            "dateActiveStatus",
            "dateInactiveStatus",
          ].includes(key)
        ) {
          obj[key] = getFormatDate(obj[key], true);
        }
        if (key === "Indicator") {
          obj[key] = getIndicatorName(obj[key]);
        }
        if (key === "isCombining") {
          obj[key] = obj[key] ? "+" : "-";
        }
        if (key === "Dealer") {
          obj[key] = get(obj, "Dealers.DealerName", "");
        }
        if (key === "Direction") {
          obj[key] = obj[key].name;
        }
        if (key === "gender") {
          obj[key] = t(`k.${obj[key]}`);
        }
        if (
          key === "importance" ||
          key === "role" ||
          key === "statusDeleteEducationProgram"
        ) {
          obj[key] = t(`k.${obj[key]}`);
        }
        if (
          key === "status" ||
          key === "statusCreatedPosition" ||
          key === "statusEditPosition"
        ) {
          obj[key] = t(`k.${obj[key]}`);
        }
        if (key === "endOrderNumber") {
          obj[key] = obj[key] ? obj[key] : "";
        }
        if (key === "RequiredEmail") {
          obj[key] = obj[key] ? t("k.yes") : t("k.no");
        }

        if (key === "__typename") {
          obj[key] = data.event === "set_staff_data" ? "Staff" : obj[key];
        }
        if (Number.isInteger(Number(key))) {
          obj["role"] = obj[key] ? obj[key] : "";
        }
      }
      return obj;
    };

    if (!prevValue && !nextValue) {
      return !!description ? description : t("logs.no_changes");
    }

    const next = changeFormatData(nextValue);

    if (!prevValue && nextValue) return logFieldResolver(next);

    const prev = changeFormatData(prevValue);
    if (prevValue && !nextValue) {
      return logFieldResolver(prev);
    }

    const diff = diffJSON(prev, next);
    if (!isNull(diff) && Object.keys(diff).length > 0) {
      return logFieldResolver(diff, data.event, LOG_RESOLVE_REASON.compare);
    }
    return t("logs.no_changes");
  };

  const { total, items } = get(data, "LogGetter.getLogs", {
    items: [],
    total: 0,
  });

  const list =
    items.length > 0
      ? items.map((el) => {
          return {
            ...el,
            event: t(`events.${el.event}`),
            user: get(el, "User.fullname", ""),
            createdAt: getFormatDate(el.createdAt, false),
            data: getDiffs(el),
          };
        })
      : [];

  const setEvent = (value: string | number) => {
    setData("event", value);
  };

  // VERSION_CONTROL_CAMERA_CFG
  const eventOptionsExclude = get(
    useVersionContextSpecificSet(),
    "page.logs.excludeLogsOptions",
    [],
  );

  // VERSION_CONTROL_CAMERA_CFG
  // Убрать фильтр при очистке VCS
  const preparedOptions = EVENT_OPTIONS.filter(
    (option) => !eventOptionsExclude.includes(option),
  ).map((option) => selectFilterOption(option, "events"));

  // Если событие из URL исключено через VCS, добавляем его опцию,
  // чтобы Select показывал переведённый label, а не сырое значение
  const selectOptions =
    event && !preparedOptions.some((option) => option.value === event)
      ? [selectFilterOption(event, "events"), ...preparedOptions]
      : preparedOptions;

  return (
    <Wrapper.Content
      title={t("logs.title")}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("log.getcronlogs") && (
            <Button
              onClick={goIntegrationsLogs}
              size={"middle"}
              color={"secondary"}
            >
              {t("logs.integrations")}
            </Button>
          )}
          {hasPermission("log.email.get") && (
            <Button onClick={goBack} size={"middle"} color={"secondary"}>
              {t("logs.titleEmail")}
            </Button>
          )}
          {hasPermission("log.users.activity") && (
            <Button onClick={goLogsIvideon} size={"middle"} color={"secondary"}>
              {t("logs.ivideon")}
            </Button>
          )}
        </Flex>
      }
    >
      <Flex gap={10} className={s.filtersRow}>
        <UserSearchFilter openModal={openModal} value={searchText} />
        <Select
          className={s.eventSelect}
          showSearch
          onChange={setEvent}
          options={selectOptions}
          value={event || ""}
          filterOption={filterOptionByLabel}
        />
      </Flex>
      <TableV2
        wrapperClassName={s.tableWrapper}
        options={LOG_OPTIONS}
        dataSource={list}
        loading={loading}
        type="logs"
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
    </Wrapper.Content>
  );
};

export default Logs;
